const firstPaintImagePromises = new Map<string, Promise<void>>();

export const FIRST_PAINT_IMAGE_TIMEOUT_MS = 8000;

/**
 * Keeps a lazy route's existing loading boundary visible until a required hero
 * image is decoded. Failures resolve so an unavailable decorative asset can
 * never strand navigation.
 */
export function preloadFirstPaintImage(src: string): Promise<void> {
  if (typeof Image === 'undefined') return Promise.resolve();

  const existing = firstPaintImagePromises.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve) => {
    const image = new Image();
    let settled = false;
    const timeoutId = setTimeout(finish, FIRST_PAINT_IMAGE_TIMEOUT_MS);

    function finish() {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve();
    }

    const decodeThenFinish = () => {
      if (typeof image.decode !== 'function') {
        finish();
        return;
      }
      void image.decode().then(finish).catch(finish);
    };

    image.decoding = 'sync';
    image.fetchPriority = 'high';
    image.onload = decodeThenFinish;
    image.onerror = finish;
    image.src = src;

    if (image.complete) decodeThenFinish();
  });

  firstPaintImagePromises.set(src, promise);
  return promise;
}
